import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import PaletteIcon from '@mui/icons-material/Palette';
import GridOnIcon from '@mui/icons-material/GridOn';
import GridOffIcon from '@mui/icons-material/GridOff';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import * as Cesium from 'cesium';
import ToolPanel from '../components/ToolPanel';
import TipsWindow from '../components/TipsWindow';
import { useTip } from '../hooks/useTips';
import { LIST_STYLES } from '../styles/viewerStyles';
import { getCesiumAssetMetadata } from '../../utils/cesiumManagement';
import {
  DEFAULT_TREE_COLOR,
  TREE_CYLINDER_HEIGHT,
  TREE_HEIGHT_OFFSET,
  applyTranslationToTreeEntities,
  buildTreeGeoJsonFromEntities,
  createTreeEntities,
  downloadGeoJson,
  parseTreesFromGeoJson
} from '../utils/treeCylinders';
import {
  addFittedCylinderFromPoints,
  createPointCloudSamplerState,
  disposePointCloudSampler as disposePointCloudSamplerUtil,
  ensurePointCloudSampler as ensurePointCloudSamplerUtil,
  samplePointCloudBox as samplePointCloudBoxUtil
} from '../utils/pointCloudSampling';
import { initGeo, applyLocalTransform } from '../hooks/useTransformationTool';

const SMALL_TILESET_RADIUS = 50;

// Legacy datasets processed before the EPSG-based geoid undulation pipeline existed:
// their GeoJSON elevations are orthometric, so cylinders need a constant
// ellipsoidal lift to sit on the point cloud. Match by the source filename
// (Cesium asset name) since the data records have no flag for this.
const LEGACY_ORTHOMETRIC_DATASETS = new Set([
  'HM01_ellipsoid.laz',
  'HM02_ellipsoid.laz',
  'HM03_ellipsoid.laz',
  'HM04_ellipsoid.laz',
  'HM05_ellipsoid.laz',
]);

function ModelsTools({ viewer, token, tileset, pointCloudTilesetId, resultsAssetId, toggle3DModelsPanel, DRAWER_WIDTH, panelOpen = false, pointValues = null, pointGeoRef = null, onAssetTypesResolved = null }) {
  const { t } = useTranslation();
  const [modelsLoading, setModelsLoading] = useState({});
  const [modelsVisibility, setModelsVisibility] = useState(() => {
    const init = {};
    (resultsAssetId || []).forEach(id => { init[id] = false; });
    return init;
  });
  const [assetNames, setAssetNames] = useState({});
  const [assetTypes, setAssetTypes] = useState({});
  const [meshColors, setMeshColors] = useState(() => {
    const init = {};
    (resultsAssetId || []).forEach(id => { init[id] = '#ffffff'; });
    return init;
  });
  const [wireframeMode, setWireframeMode] = useState(() => {
    const init = {};
    (resultsAssetId || []).forEach(id => { init[id] = false; });
    return init;
  });
  const [loadedTilesets, setLoadedTilesets] = useState({});
  const [loadedGeoJsons, setLoadedGeoJsons] = useState({});
  const [pointCloudAssetName, setPointCloudAssetName] = useState('');
  const pointPickHandlerRef = useRef(null);
  // Offscreen 3D tiles sampler for point cloud point queries.
  const pointCloudSamplerRef = useRef(createPointCloudSamplerState());

  // When the viewer instance changes, all previously added scene primitives have
  // been destroyed by Cesium. Clear stale tileset/GeoJSON references so the render
  // never touches a destroyed object (whose _root is undefined).
  const prevViewerRef = useRef(null);
  useEffect(() => {
    if (prevViewerRef.current === viewer) return;
    prevViewerRef.current = viewer;
    setLoadedTilesets({});
    setLoadedGeoJsons({});
    setModelsVisibility(prev => Object.fromEntries(Object.keys(prev).map(id => [id, false])));
    setModelsLoading({});
  }, [viewer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch asset names
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const names = {};
      const types = {};
      for (const assetId of resultsAssetId || []) {
        try {
          const metadata = await getCesiumAssetMetadata(assetId, token);
          names[assetId] = metadata.name || `Asset ${assetId}`;
          if (metadata?.type) {
            types[assetId] = String(metadata.type).toUpperCase();
          }
        } catch (error) {
          console.warn(`Failed to fetch metadata for asset ${assetId}:`, error);
          names[assetId] = `Asset ${assetId}`;
        }
      }
      if (!cancelled) {
        setAssetNames(names);
        setAssetTypes(types);
        onAssetTypesResolved?.(types);
        setMeshColors((prev) => {
          const next = { ...prev };
          (resultsAssetId || []).forEach((assetId) => {
            const isGeoJson = types[assetId] === 'GEOJSON';
            if (isGeoJson && (!next[assetId] || next[assetId] === '#ffffff')) {
              next[assetId] = '#ffff00';
            } else if (next[assetId] === undefined) {
              next[assetId] = '#ffffff';
            }
          });
          return next;
        });
      }
    };
    if (resultsAssetId && token) run();
    return () => { cancelled = true; };
  }, [resultsAssetId, token]);

  // Do not remove loaded tilesets on unmount; models persist independent of panel visibility

  const applyPhongStyling = useCallback((tileset, assetId, overrideColor = null, overrideWireframe = null) => {
    if (!tileset) return;
    const color = overrideColor || meshColors[assetId] || '#ffffff';
    const wireframe = overrideWireframe !== null ? overrideWireframe : (wireframeMode[assetId] || false);
    try {
      if (wireframe) {
        tileset.style = new Cesium.Cesium3DTileStyle({ color: `color('${color}', 0.8)`, show: true });
        if (tileset.readyPromise) {
          tileset.readyPromise.then(() => { tileset.debugWireframe = true; }).catch((e) => {
            console.warn('Failed to set wireframe mode:', e);
          });
        } else { tileset.debugWireframe = true; }
      } else {
        tileset.style = new Cesium.Cesium3DTileStyle({ color: `color('${color}')`, show: true });
        tileset.debugWireframe = false;
      }
    } catch (error) {
      console.warn('Failed to apply tileset styling:', error);
    }
  }, [meshColors, wireframeMode]);

  const resolveTreeColor = useCallback((assetId, overrideColor = null) => {
    const colorValue = overrideColor ?? meshColors[assetId];
    try {
      return colorValue
        ? Cesium.Color.fromCssColorString(colorValue).withAlpha(DEFAULT_TREE_COLOR.alpha)
        : DEFAULT_TREE_COLOR;
    } catch (error) {
      // Invalid CSS color string - fall back to default
      console.warn(`Invalid color value "${colorValue}":`, error);
      return DEFAULT_TREE_COLOR;
    }
  }, [meshColors]);

  const clearPointPickHandler = useCallback(() => {
    if (pointPickHandlerRef.current) {
      try {
        pointPickHandlerRef.current.destroy();
      } catch (error) {
        // Handler may already be destroyed
        console.warn('Failed to destroy point pick handler:', error);
      }
      pointPickHandlerRef.current = null;
    }
  }, []);

  const disposePointCloudSampler = useCallback(() => {
    disposePointCloudSamplerUtil(pointCloudSamplerRef.current);
  }, []);

  const ensurePointCloudSampler = useCallback(() => (
    ensurePointCloudSamplerUtil({
      sampler: pointCloudSamplerRef.current,
      pointCloudTilesetId,
      token
    })
  ), [pointCloudTilesetId, token]);

  const samplePointCloudBox = useCallback((center) => (
    samplePointCloudBoxUtil({
      sampler: pointCloudSamplerRef.current,
      center,
      pointCloudTilesetId,
      token
    })
  ), [pointCloudTilesetId, token]);

  const addFittedCylinder = useCallback((assetId, pointArray) => {
    addFittedCylinderFromPoints({
      viewer,
      tileset,
      assetId,
      pointArray,
      geo: loadedGeoJsons[assetId],
      pointGeo: pointGeoRef?.current,
      pointValues,
      resolveTreeColor,
      setLoadedGeoJsons
    });
  }, [viewer, tileset, loadedGeoJsons, pointGeoRef, pointValues, resolveTreeColor, setLoadedGeoJsons]);

  const loadTilesetFromION = useCallback(async (assetId) => {
    if (!viewer || !token) return null;
    try {
      const loadedTileset = await Cesium.Cesium3DTileset.fromIonAssetId(assetId, { accessToken: token, backFaceCulling: false });
      if (loadedTileset.readyPromise) await loadedTileset.readyPromise;

      if (pointValues) {
        applyLocalTransform(loadedTileset, initGeo(loadedTileset), pointValues);
      }

      applyPhongStyling(loadedTileset, assetId);
      return loadedTileset;
    } catch (e) {
      console.error(`Failed to load tileset ${assetId}:`, e);
      return null;
    }
  }, [viewer, token, pointValues, applyPhongStyling]);

  const addTilesetToScene = useCallback((tileset) => {
    if (!viewer || !tileset) return false;
    try {
      viewer.scene.primitives.add(tileset);
      return true;
    } catch (error) {
      console.warn('Failed to add tileset to scene:', error);
      return false;
    }
  }, [viewer]);

  const setTilesetVisibility = useCallback((tileset, visible) => {
    if (!tileset) return false;
    try {
      tileset.show = visible;
      return true;
    } catch (error) {
      console.warn('Failed to set tileset visibility:', error);
      return false;
    }
  }, []);

  const setGeoJsonVisibility = useCallback((assetId, visible) => {
    const geo = loadedGeoJsons[assetId];
    if (!geo || !Array.isArray(geo.entities)) {
      return false;
    }
    try {
      geo.entities.forEach((entity) => { entity.show = visible; });
      return true;
    } catch (error) {
      console.warn(`Failed to set GeoJSON visibility for asset ${assetId}:`, error);
      return false;
    }
  }, [loadedGeoJsons]);

  const loadGeoJsonFromION = useCallback(async (assetId) => {
    if (!viewer || !token) return null;
    try {
      const resource = await Cesium.IonResource.fromAssetId(assetId, { accessToken: token });
      const geoJson = await resource.fetchJson();
      const trees = parseTreesFromGeoJson(geoJson);
      if (!trees.length) {
        console.warn(`GeoJSON asset ${assetId} contains no trees`);
        return null;
      }

      // Fallback: if the user toggled visibility before the mount-time fetch
      // settled, resolve the point cloud name inline so the legacy hack still
      // applies. Cheap second call (Cesium caches asset metadata).
      let resolvedName = pointCloudAssetName;
      if (!resolvedName && pointCloudTilesetId) {
        try {
          const md = await getCesiumAssetMetadata(pointCloudTilesetId, token);
          resolvedName = md?.name || '';
          if (resolvedName) setPointCloudAssetName(resolvedName);
        } catch (error) {
          console.warn(`Failed to fetch metadata for point cloud asset ${pointCloudTilesetId}:`, error);
        }
      }
      const heightOffset = LEGACY_ORTHOMETRIC_DATASETS.has(resolvedName)
        ? TREE_HEIGHT_OFFSET
        : 0;

      const entities = createTreeEntities(viewer, trees, {
        color: resolveTreeColor(assetId),
        cylinderHeight: TREE_CYLINDER_HEIGHT,
        heightOffset
      });

      // Mirror the tileset's ENU translation so cylinders sit on the point cloud.
      applyTranslationToTreeEntities(entities, pointGeoRef?.current, pointValues);

      return { entities, trees };
    } catch (e) {
      console.error(`Failed to load GeoJSON ${assetId}:`, e);
      return null;
    }
  }, [viewer, token, pointValues, pointGeoRef, resolveTreeColor, pointCloudAssetName, pointCloudTilesetId]);

  const waitForNextRender = useCallback(async () => {
    if (!viewer || !viewer.scene) return;
    await new Promise((resolve) => {
      const cb = () => {
        viewer.scene.postRender.removeEventListener(cb);
        resolve();
      };
      viewer.scene.postRender.addEventListener(cb);
      viewer.scene.requestRender?.();
    });
  }, [viewer]);

  useEffect(() => () => {
    clearPointPickHandler();
    disposePointCloudSampler();
  }, [clearPointPickHandler, disposePointCloudSampler]);

  useEffect(() => {
    disposePointCloudSampler();
  }, [token, pointCloudTilesetId, disposePointCloudSampler]);

  // Resolve the point cloud's source filename so we can apply the legacy
  // orthometric-height hack for HM01..HM05_ellipsoid.laz.
  useEffect(() => {
    let cancelled = false;
    if (!pointCloudTilesetId || !token) {
      setPointCloudAssetName('');
      return undefined;
    }
    getCesiumAssetMetadata(pointCloudTilesetId, token)
      .then((metadata) => {
        if (!cancelled) setPointCloudAssetName(metadata?.name || '');
      })
      .catch((error) => {
        console.warn(`Failed to fetch metadata for point cloud asset ${pointCloudTilesetId}:`, error);
      });
    return () => { cancelled = true; };
  }, [pointCloudTilesetId, token]);

  // Keep loaded GeoJSON cylinders glued to the point cloud whenever the user
  // adjusts the dataset's translation (tx/ty/tz) in the Transformation panel.
  useEffect(() => {
    const geo = pointGeoRef?.current;
    if (!geo) return;
    Object.values(loadedGeoJsons).forEach((entry) => {
      applyTranslationToTreeEntities(entry?.entities, geo, pointValues);
    });
    viewer?.scene?.requestRender?.();
  }, [pointValues, loadedGeoJsons, pointGeoRef, viewer]);

  const ensureAssetInfo = useCallback(async (assetId) => {
    const existingType = assetTypes[assetId];
    if (existingType) {
      return String(existingType).toUpperCase();
    }

    if (!token) return '';

    try {
      const metadata = await getCesiumAssetMetadata(assetId, token);
      const nextType = metadata?.type ? String(metadata.type).toUpperCase() : '';
      if (nextType) {
        setAssetTypes(prev => ({ ...prev, [assetId]: nextType }));
      }
      if (metadata?.name) {
        setAssetNames(prev => ({ ...prev, [assetId]: prev[assetId] || metadata.name }));
      }
      return nextType;
    } catch (error) {
      console.warn(`Failed to fetch asset info for ${assetId}:`, error);
      return '';
    }
  }, [assetTypes, token, setAssetTypes, setAssetNames]);

  const withModelLoading = useCallback(async (assetId, task) => {
    setModelsLoading(prev => ({ ...prev, [assetId]: true }));
    try {
      return await task();
    } finally {
      setModelsLoading(prev => ({ ...prev, [assetId]: false }));
    }
  }, [setModelsLoading]);

  const showGeoJson = useCallback(async (assetId) => {
    if (loadedGeoJsons[assetId]) {
      const ok = setGeoJsonVisibility(assetId, true);
      if (ok) setModelsVisibility(prev => ({ ...prev, [assetId]: true }));
      return;
    }

    await withModelLoading(assetId, async () => {
      const geo = await loadGeoJsonFromION(assetId);
      if (geo && geo.entities && geo.entities.length) {
        setLoadedGeoJsons(prev => ({ ...prev, [assetId]: geo }));
        setModelsVisibility(prev => ({ ...prev, [assetId]: true }));
        await waitForNextRender();
      }
    });
  }, [
    loadedGeoJsons,
    setGeoJsonVisibility,
    setModelsVisibility,
    withModelLoading,
    loadGeoJsonFromION,
    waitForNextRender,
    setLoadedGeoJsons
  ]);

  const hideGeoJson = useCallback((assetId) => {
    setGeoJsonVisibility(assetId, false);
    setModelsVisibility(prev => ({ ...prev, [assetId]: false }));
  }, [setGeoJsonVisibility, setModelsVisibility]);

  const showTileset = useCallback(async (assetId) => {
    const existingTileset = loadedTilesets[assetId];
    if (existingTileset) {
      const ok = setTilesetVisibility(existingTileset, true);
      if (ok) setModelsVisibility(prev => ({ ...prev, [assetId]: true }));
      return;
    }

    await withModelLoading(assetId, async () => {
      const ts = await loadTilesetFromION(assetId);
      if (!ts) return;

      const added = addTilesetToScene(ts);
      if (!added) return;

      setTilesetVisibility(ts, true);
      setLoadedTilesets(prev => ({ ...prev, [assetId]: ts }));
      setModelsVisibility(prev => ({ ...prev, [assetId]: true }));
      await waitForNextRender();
    });
  }, [
    loadedTilesets,
    setTilesetVisibility,
    setModelsVisibility,
    withModelLoading,
    loadTilesetFromION,
    addTilesetToScene,
    waitForNextRender,
    setLoadedTilesets
  ]);

  const hideTileset = useCallback((assetId) => {
    const ts = loadedTilesets[assetId];
    if (ts) setTilesetVisibility(ts, false);
    setModelsVisibility(prev => ({ ...prev, [assetId]: false }));
  }, [loadedTilesets, setTilesetVisibility, setModelsVisibility]);

  const buildGeoJsonForAsset = useCallback((assetId) => {
    const geo = loadedGeoJsons[assetId];
    return buildTreeGeoJsonFromEntities(geo?.entities, viewer);
  }, [loadedGeoJsons, viewer]);

  const saveGeoJsonToFile = useCallback((assetId) => {
    const geojson = buildGeoJsonForAsset(assetId);
    if (!geojson) {
      console.warn('No GeoJSON data available to save for asset', assetId);
      return;
    }
    const filename = `${assetNames[assetId] || 'trees'}-${assetId}.geojson`;
    downloadGeoJson(geojson, filename);
  }, [assetNames, buildGeoJsonForAsset]);

  const startPointSelection = useCallback((assetId) => {
    if (!viewer || !viewer.scene || !viewer.canvas) return;
    if (!tileset || (tileset.isDestroyed && tileset.isDestroyed()) || tileset.show === false) {
      console.warn('Point cloud tileset is not available.');
      return;
    }
    const geo = loadedGeoJsons[assetId];
    if (!geo || !Array.isArray(geo.entities) || !geo.entities.length) {
      console.warn('GeoJSON asset is not loaded yet.');
      return;
    }

    void ensurePointCloudSampler();
    clearPointPickHandler();
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
    pointPickHandlerRef.current = handler;

    handler.setInputAction(async (click) => {
      const picked = viewer.scene.pick(click.position);
      const pickedTileset = picked?.tileset || picked?.primitive?.tileset || picked?.primitive;
      if (pickedTileset !== tileset) {
        console.warn('Select a point from the main point cloud.');
        return;
      }

      const position = viewer.scene.pickPosition(click.position);
      if (!position) {
        console.warn('No depth available at this pixel');
        return;
      }

      const carto = Cesium.Cartographic.fromCartesian(position);
      const coords = {
        lon: Cesium.Math.toDegrees(carto.longitude),
        lat: Cesium.Math.toDegrees(carto.latitude),
        elevation: carto.height,
      };
      console.log('Selected point coordinates:', coords);
      clearPointPickHandler();

      try {
        const result = await samplePointCloudBox(position);
        if (result) {
          console.log('Point cloud box points:', { count: result.count, points: result.points });
          const pointsForFit = [];
          for (let i = 0; i < result.points.length; i += 3) {
            pointsForFit.push([result.points[i], result.points[i + 1], result.points[i + 2]]);
          }
          addFittedCylinder(assetId, pointsForFit);
        }
      } catch (error) {
        console.warn('Failed to sample point cloud box:', error);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    handler.setInputAction(() => {
      clearPointPickHandler();
    }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);
  }, [viewer, tileset, loadedGeoJsons, clearPointPickHandler, ensurePointCloudSampler, samplePointCloudBox, addFittedCylinder]);

  const toggleModelVisibility = useCallback(async (modelId) => {
    const currentVisibility = !!modelsVisibility[modelId];
    const assetType = await ensureAssetInfo(modelId);
    const isGeoJson = assetType === 'GEOJSON';

    if (isGeoJson) {
      if (currentVisibility) {
        hideGeoJson(modelId);
      } else {
        await showGeoJson(modelId);
      }
      return;
    }

    if (currentVisibility) {
      hideTileset(modelId);
    } else {
      await showTileset(modelId);
    }
  }, [modelsVisibility, ensureAssetInfo, showGeoJson, hideGeoJson, showTileset, hideTileset]);

  const updateMeshColor = useCallback((assetId, color) => {
    setMeshColors(prev => ({ ...prev, [assetId]: color }));

    const assetType = assetTypes[assetId] ? String(assetTypes[assetId]).toUpperCase() : '';
    if (assetType === 'GEOJSON') {
      const geo = loadedGeoJsons[assetId];
      if (geo && geo.entities) {
        const resolvedColor = resolveTreeColor(assetId, color);
        geo.entities.forEach((entity) => {
          if (entity?.cylinder) {
            entity.cylinder.material = resolvedColor;
          }
        });
      }
      return;
    }

    const ts = loadedTilesets[assetId];
    if (ts) applyPhongStyling(ts, assetId, color, null);
  }, [assetTypes, loadedGeoJsons, resolveTreeColor, loadedTilesets, applyPhongStyling]);

  const toggleWireframe = useCallback((assetId) => {
    const assetType = assetTypes[assetId] ? String(assetTypes[assetId]).toUpperCase() : '';
    if (assetType === 'GEOJSON') {
      return;
    }

    const newWire = !(wireframeMode[assetId]);
    setWireframeMode(prev => ({ ...prev, [assetId]: newWire }));
    const ts = loadedTilesets[assetId];
    if (ts) applyPhongStyling(ts, assetId, null, newWire);
  }, [assetTypes, wireframeMode, loadedTilesets, applyPhongStyling]);

  const zoomToTileset = useCallback((assetId) => {
    const ts = loadedTilesets[assetId];
    if (!viewer || !ts) return;
    try {
      viewer.flyTo(ts, { duration: 1.2, offset: new Cesium.HeadingPitchRange(-1, -0.3, 0) });
      //viewer.camera.viewBoundingSphere(ts.boundingSphere, new Cesium.HeadingPitchRange(0, -0.5, 0));
    } catch (error) {
      console.warn('Failed to zoom to tileset.', error);
    }
  }, [viewer, loadedTilesets]);

  // Tips integration
  const { isOpen: isTipOpen, title: tipTitle, markdownBody: tipBody, close: closeTip } = useTip('models', panelOpen);

  // Render nothing when panel is closed, but keep component mounted to retain state
  if (!panelOpen) {
    return null;
  }

  return (
    <>
    <TipsWindow
      isOpen={isTipOpen}
      title={tipTitle}
      markdownBody={tipBody}
      onClose={({ doNotShowAgain }) => closeTip(doNotShowAgain)}
    />
    <ToolPanel
      title={t('Products') || 'Products'}
      onClose={toggle3DModelsPanel}
      width={DRAWER_WIDTH}
      contentPadding={1.5}
    >
      {/* List of Products */}
      <Box sx={{ pt: 0., pb: 0. }}>
      {resultsAssetId.map((assetId) => {
        const assetType = assetTypes[assetId] ? String(assetTypes[assetId]).toUpperCase() : '';
        const isGeoJson = assetType === 'GEOJSON';
        const colorValue = meshColors[assetId] || '#ffffff';
        const isVisible = modelsVisibility[assetId];
        const _ts = loadedTilesets[assetId];
        const tilesetRadius = (_ts && !_ts.isDestroyed?.()) ? _ts.boundingSphere?.radius : undefined;
        const isSmallTileset = !isGeoJson
          && Number.isFinite(tilesetRadius)
          && tilesetRadius > 0
          && tilesetRadius <= SMALL_TILESET_RADIUS;
        const canSave = isGeoJson && isVisible && !!loadedGeoJsons[assetId];
        const pointCloudReady = !!tileset
          && !(tileset.isDestroyed && tileset.isDestroyed())
          && tileset.show !== false
          && !!pointCloudTilesetId
          && !!token;
        const canPick = isGeoJson && isVisible && !!loadedGeoJsons[assetId] && pointCloudReady;
        const pickTitle = !pointCloudReady
          ? 'Point cloud is not available'
          : (canPick ? 'Select point from point cloud' : (t('LoadFirst') || 'Load the product first'));

        return (
          <Box key={assetId} sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb:0.5,
            ml: 0.,
            mr: 0.75,
            bgcolor: 'rgba(255,255,255,0.05)',
            borderRadius: 1,
            border: '1px solid rgba(255,255,255,0.1)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.1)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              {modelsLoading[assetId] ? (
                <Box sx={{ p: 0.5, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }}>
                  <CircularProgress
                    size={16}
                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                  />
                </Box>
              ) : (
                <Checkbox
                  checked={isVisible === true}
                  onChange={() => toggleModelVisibility(assetId)}
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-checked': {
                      color: '#fff',
                    },
                    p: 0.5,
                    mr: 1,
                  }}
                />
              )}
              <Typography variant="caption" sx={{
                color: '#fff',
                fontSize: '0.7rem',
                flex: 1,
              }}>
                {assetNames[assetId] || `Loading...`}
              </Typography>
            </Box>

            {/* Color Picker and Wireframe Controls - always show for available assets */}
            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
              {isSmallTileset && (
                <IconButton
                  size="small"
                  onClick={() => zoomToTileset(assetId)}
                  disabled={!isVisible}
                  sx={{
                    color: isVisible ? '#ffffff' : 'rgba(255,255,255,0.3)',
                    p: 0.5,
                    '&:hover': {
                      bgcolor: isVisible ? 'rgba(182, 182, 182, 0.2)' : 'transparent',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.3)',
                    }
                  }}
                  title={isVisible ? 'Zoom to mesh' : 'Load the mesh first'}
                >
                  <ZoomInMapIcon sx={{ fontSize: '12px' }} />
                </IconButton>
              )}
              {isGeoJson && (
                <IconButton
                  size="small"
                  onClick={() => startPointSelection(assetId)}
                  disabled={!canPick}
                  sx={{
                    color: canPick ? '#ffffff' : 'rgba(255,255,255,0.3)',
                    p: 0.5,
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.3)',
                    }
                  }}
                  title={pickTitle}
                >
                  <AddIcon sx={{ fontSize: '12px' }} />
                </IconButton>
              )}
              {/* Color Picker Icon - using label wrapper for direct input access */}
              <label
                style={{
                  display: 'inline-block',
                  cursor: isVisible ? 'pointer' : 'not-allowed'
                }}
              >
                <input
                  type="color"
                  value={colorValue}
                  onChange={(e) => {
                    const newColor = e.target.value;
                    e.stopPropagation();
                    updateMeshColor(assetId, newColor);
                  }}
                  onInput={(e) => {
                    const newColor = e.target.value;
                    updateMeshColor(assetId, newColor);
                  }}
                  disabled={!isVisible}
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    width: '32px',
                    height: '32px',
                    cursor: 'pointer',
                    zIndex: 1
                  }}
                />
                <IconButton
                  size="small"
                  disabled={!isVisible}
                  sx={{
                    color: isVisible ? '#ffffff' : 'rgba(255,255,255,0.3)',
                    p: 0.5,
                    '&:hover': {
                      bgcolor: isVisible ? 'rgba(182, 182, 182, 0.2)' : 'transparent',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.3)',
                    },
                    pointerEvents: 'none'
                  }}
                  title={isVisible ? 'Change color' : 'Load the product first'}
                >
                  <PaletteIcon sx={{ fontSize: '12px' }} />
                </IconButton>
              </label>

              {isGeoJson && (
                <IconButton
                  size="small"
                  onClick={() => saveGeoJsonToFile(assetId)}
                  disabled={!canSave}
                  sx={{
                    color: canSave ? '#ffffff' : 'rgba(255,255,255,0.3)',
                    p: 0.5,
                    mr:0.5,
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.3)',
                    }
                  }}
                  title={canSave ? (t('Save') || 'Save') : (t('LoadFirst') || 'Load the product first')}
                >
                  <SaveIcon sx={{ fontSize: '12px' }} />
                </IconButton>
              )}

              {!isGeoJson && (
                <IconButton
                  size="small"
                  onClick={() => toggleWireframe(assetId)}
                  disabled={!isVisible}
                  sx={{
                    color: isVisible ? '#ffffff' : 'rgba(255,255,255,0.3)',
                    p: 0.5,
                    '&:hover': {
                      bgcolor: isVisible ? 'rgba(182, 182, 182, 0.2)' : 'transparent',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.3)',
                    },
                    bgcolor: isVisible && wireframeMode[assetId] ? 'rgba(76, 175, 80, 0.2)' : 'transparent',
                  }}
                  title={!isVisible
                    ? 'Load the mesh first'
                    : (wireframeMode[assetId] ? 'Disable wireframe' : 'Enable wireframe')
                  }
                >
                  {wireframeMode[assetId] ? (
                    <GridOnIcon sx={{ fontSize: '12px' }} />
                  ) : (
                    <GridOffIcon sx={{ fontSize: '12px' }} />
                  )}
                </IconButton>
              )}
            </Box>
          </Box>
        );
      })}
      </Box>
    </ToolPanel>
    </>
  );
}

ModelsTools.propTypes = {
  viewer: PropTypes.object,
  token: PropTypes.string,
  tileset: PropTypes.object,
  pointCloudTilesetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resultsAssetId: PropTypes.array.isRequired,
  toggle3DModelsPanel: PropTypes.func.isRequired,
  DRAWER_WIDTH: PropTypes.number.isRequired,
  panelOpen: PropTypes.bool,
  pointValues: PropTypes.object,
  pointGeoRef: PropTypes.shape({ current: PropTypes.object }),
  onAssetTypesResolved: PropTypes.func,
};

export default ModelsTools;
